import { AxiosInstance, AxiosPromise, AxiosRequestConfig, AxiosResponse } from 'axios';
import { AttributeI, AttributePatch } from '~src/interfaces/attributes.types';
import { CompanyI } from '~src/interfaces/company.interface';
import { Segment, UserSegment } from '~src/interfaces/segment.interface';
import { ContentLocalesI } from '~src/interfaces/session.types';
import { User } from '~src/interfaces/user.interface';

import { FetchAttributesParams } from '~src/modules/attributes/actions/attributes.actions';
import { AttributesRespI } from '~src/modules/attributes/interfaces';
import { SortingValues } from '~src/modules/reports/shared/constants/filterAndSortingKeys';
import { FetchSegmentsParams } from '~src/modules/surveys/actions/segmentsActions';
import { Otp, UsersRespI } from '~src/modules/users/interfaces';
import { getUrlFromObject } from '~src/utils';
import { baseApi } from '~src/services/ApiController';
import logger from '~src/services/logger/LlsLogger';
import { SelfSignupFormI } from '~src/store/session/interfaces';

export interface CreatePasswordValues {
  otp: string;
  identifier: string;
  password: string;
}

export interface ChangePasswordValues {
  oldPassword?: string;
  password: string;
}

export interface GetUserAsSegment {
  as_segments: true;
}

export interface GetUserParams {
  setting_attributes?: boolean;
  custom_attributes?: boolean;
  manager_attributes?: boolean;
  permissions?: boolean;
}

export interface GetUsersParams {
  page?: number;
  sort?: SortingValues;
  'filter.user'?: string;
  per_page?: number;
}

type GetUserResponse<T> = T extends GetUserAsSegment ? UserSegment[] : User[];

export class Base {
  constructor(private axios: AxiosInstance) {}

  getSegments(params: FetchSegmentsParams, config?: AxiosRequestConfig): AxiosPromise<Segment[]> {
    const query = getUrlFromObject({ ...params, include: 'user_count' }, ['uuids', 'type']);
    return this.axios.get(`/segments/search${query}`, config);
  }

  getUser(uuid: string, params: GetUserParams, config?: AxiosRequestConfig): AxiosPromise<User> {
    const query = getUrlFromObject({...params});
    return this.axios.get(`/users/${uuid}${query}`, config);
  }

  getUserTeammembers<T extends GetUserParams | GetUserAsSegment>(uuid: string, params: T, config?: AxiosRequestConfig): Promise<AxiosResponse<GetUserResponse<T>>> {
    const query = getUrlFromObject({...params});
    return this.axios.get(`/users/${uuid}/team${query}`, config);
  }

  getUsers(params: GetUsersParams, config?: AxiosRequestConfig): AxiosPromise<UsersRespI> {
    return this.axios.get(`/users${getUrlFromObject({...params})}`, config);
  }

  getAttributesPaginated(
    params: FetchAttributesParams = {},
    config?: AxiosRequestConfig,
  ): AxiosPromise<AttributesRespI> {
    return this.axios.get(`/attributes/list${getUrlFromObject({...params})}`, config);
  }

  getAttributes(): AxiosPromise<AttributeI[]> {
    return this.axios.get(`/attributes`);
  }

  patchAttribute(uuid: string, properties: Partial<AttributePatch>): AxiosPromise<AttributeI> {
    return this.axios.patch(`/attributes/${uuid}`, properties);
  }

  postAttribute(attribute: Pick<AttributeI, 'name'|'internal_type'>): AxiosPromise<AttributeI> {
    return this.axios.post('/attributes', attribute);
  }

  deleteAttribute(uuid: string): AxiosPromise<void> {
    return this.axios.delete(`/attributes/${uuid}`)
  }

  getCompany(config?: AxiosRequestConfig): AxiosPromise<CompanyI> {
    return this.axios.get('company', config);
  }

  passwordResetForEmail(values: { email: string }) {
    return baseApi.post('/password/reset-for-email', values).catch((error) => {
      logger.info('Error on getting new password', {
        error,
        info: {
          componentStack: 'session/actions.ts',
        },
      });
      return error;
    });
  }

  passwordSetFromOtp(values: CreatePasswordValues): AxiosPromise<number> {
    return this.axios.post('/password/set-from-otp', values);
  }

  passwordSet(values: ChangePasswordValues): AxiosPromise<void> {
    return this.axios.post('/password/set', values);
  }

  passwordCreateOtp(userId: string): AxiosPromise<Otp> {
    return this.axios.post('/password/create-otp', { user_uuid: userId });
  }

  getTeamSegments(
    managerUuids: string[],
    config?: AxiosRequestConfig,
  ): AxiosPromise<Record<string, Segment>> {
    return this.axios.get(`/segments/teams?uuids=${managerUuids.join(',')}`, config);
  }

  getSelfSignupPayload(tenantId: string, cycleId?: string): AxiosPromise<{
    company: CompanyI,
    selfAssignableAttributes: AttributeI[],
    companyImageDataUrl: string,
    surveyName: ContentLocalesI,
    surveyInvitation: ContentLocalesI,
  }> {
    let query = '';
    if (cycleId) {
      query = getUrlFromObject({
        cycle: cycleId,
      });
    }
    return this.axios.get(`/tenant/${tenantId}/self-signup${query}`);
  }

  selfSignUp(values: SelfSignupFormI, tenantUUID: string): AxiosPromise<void> {
    return this.axios.post(`/tenant/${tenantUUID}/users`, values);
  }
}
